"use client";

import React, { useState } from "react";
import { MapPin, GraduationCap, TrainFront, Car, House, Info, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const neighborhoods = [{
  name: "Warwick",
  tagline: "Historic charm meets modern living",
  description: "Known for its vibrant village center, apple orchards, and wineries. Warwick offers a perfect blend of rural beauty and sophisticated dining.",
  stats: {
    vibe: ["Artistic", "Historic", "Farm-to-Table"],
    schools: {
      elementary: 9,
      middle: 8,
      high: 9
    },
    commute: {
      time: "65 min",
      ease: 75,
      type: "Bus/Car"
    }
  }
}, {
  name: "Goshen",
  tagline: "The county seat with a legacy",
  description: "House to the Historic Track and LEGOLAND. Goshen features beautiful Victorian homes and a bustling downtown area with a strong sense of community.",
  stats: {
    vibe: ["Family-Centric", "Traditional", "Active"],
    schools: {
      elementary: 8,
      middle: 9,
      high: 8
    },
    commute: {
      time: "60 min",
      ease: 80,
      type: "Bus/TrainFront"
    }
  }
}, {
  name: "Monroe",
  tagline: "Lakeside living and convenience",
  description: "Centered around the beautiful Round Lake and Walton Lake. Monroe offers diverse housing options and excellent recreational facilities.",
  stats: {
    vibe: ["Suburban", "Scenic", "Convenient"],
    schools: {
      elementary: 9,
      middle: 8,
      high: 9
    },
    commute: {
      time: "50 min",
      ease: 85,
      type: "TrainFront/Bus"
    }
  }
}, {
  name: "Cornwall",
  tagline: "Hudson River views and arts",
  description: "Nestled along the Hudson River, Cornwall is known for its stunning scenery, Storm King Art Center, and a quaint, walkable Main Street.",
  stats: {
    vibe: ["Riverfront", "Creative", "Peaceful"],
    schools: {
      elementary: 9,
      middle: 9,
      high: 8
    },
    commute: {
      time: "55 min",
      ease: 70,
      type: "TrainFront (Beacon)"
    }
  }
}, {
  name: "Middletown",
  tagline: "Urban amenities in a rural setting",
  description: "A growing hub with a revitalized downtown, SUNY Orange campus, and diverse culinary scene. Offers great value and accessibility.",
  stats: {
    vibe: ["Up-and-Coming", "Diverse", "Accessible"],
    schools: {
      elementary: 6,
      middle: 7,
      high: 6
    },
    commute: {
      time: "70 min",
      ease: 80,
      type: "TrainFront/Bus"
    }
  }
}, {
  name: "Tuxedo Park",
  tagline: "Exclusive and historic enclave",
  description: "A gated village listed on the National Register of Historic Places. Known for its grand estates, privacy, and pristine lakes.",
  stats: {
    vibe: ["Exclusive", "Private", "Nature-Rich"],
    schools: {
      elementary: 9,
      middle: 9,
      high: 9
    },
    commute: {
      time: "45 min",
      ease: 90,
      type: "TrainFront"
    }
  }
}, {
  name: "Newburgh",
  tagline: "Waterfront revitalization and history",
  description: "Experience a historic city undergoing a creative renaissance, featuring stunning Hudson River views and an emerging dining scene.",
  stats: {
    vibe: ["Urban", "Historic", "Riverfront"],
    schools: {
      elementary: 5,
      middle: 5,
      high: 5
    },
    commute: {
      time: "75 min",
      ease: 65,
      type: "Ferry/Bus"
    }
  }
}, {
  name: "Chester",
  tagline: "Rural beauty and local flavors",
  description: "Home to the famous black dirt region and Castle Family Fun Center. Chester offers a mix of agricultural heritage and suburban comfort.",
  stats: {
    vibe: ["Rural", "Community", "Relaxed"],
    schools: {
      elementary: 7,
      middle: 7,
      high: 7
    },
    commute: {
      time: "60 min",
      ease: 75,
      type: "Car/Bus"
    }
  }
}, {
  name: "Washingtonville",
  tagline: "Small town heart, big community feel",
  description: "Best known as the home of Brotherhood Winery, America's oldest winery. A family-oriented village with a charming, tight-knit atmosphere.",
  stats: {
    vibe: ["Quaint", "Family-Oriented", "Historic"],
    schools: {
      elementary: 8,
      middle: 8,
      high: 8
    },
    commute: {
      time: "70 min",
      ease: 70,
      type: "Bus/Car"
    }
  }
}];
export default function LocalNeighborhoodInsights() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="local-neighborhood-insights" ref={ref} className={`py-24 bg-background transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
            Local Neighborhood Insights
          </h2>
          <p className="text-lg text-muted-foreground">
            Discover the unique character of Orange County's most sought-after
            towns. Explore school ratings, commute times, and local vibes to
            find your perfect fit.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {neighborhoods.map((town, i) => <div key={town.name} data-index={i} className="h-full">
              <Card className="h-full flex flex-col border-border/60 shadow-sm hover:shadow-md transition-shadow duration-300">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl font-serif text-primary">
                        {town.name}
                      </CardTitle>
                      <CardDescription className="mt-1 text-sm font-medium text-muted-foreground">
                        {town.tagline}
                      </CardDescription>
                    </div>
                    <MapPin className="w-5 h-5 text-secondary shrink-0" />
                  </div>
                </CardHeader>
                <CardContent className="flex-1 pb-6">
                  <Tabs defaultValue="vibe" className="w-full">
                    <TabsList className="w-full grid grid-cols-3 mb-6 bg-muted/50">
                      <TabsTrigger value="vibe">Vibe</TabsTrigger>
                      <TabsTrigger value="schools">Schools</TabsTrigger>
                      <TabsTrigger value="commute">Commute</TabsTrigger>
                    </TabsList>

                    <TabsContent value="vibe" className="space-y-4 mt-0">
                      <p className="text-sm text-muted-foreground leading-relaxed min-h-[80px]">
                        {town.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {town.stats.vibe.map((tag, idx) => <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary-foreground border border-secondary/20">
                            {tag}
                          </span>)}
                      </div>
                    </TabsContent>

                    <TabsContent value="schools" className="space-y-5 mt-0">
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs font-medium">
                            <span>Elementary</span>
                            <span className="text-muted-foreground">
                              {town.stats.schools.elementary}/10
                            </span>
                          </div>
                          <Progress value={town.stats.schools.elementary * 10} className="h-1.5 [&>[data-slot=progress-indicator]]:bg-secondary" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs font-medium">
                            <span>Middle School</span>
                            <span className="text-muted-foreground">
                              {town.stats.schools.middle}/10
                            </span>
                          </div>
                          <Progress value={town.stats.schools.middle * 10} className="h-1.5 [&>[data-slot=progress-indicator]]:bg-secondary" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs font-medium">
                            <span>High School</span>
                            <span className="text-muted-foreground">
                              {town.stats.schools.high}/10
                            </span>
                          </div>
                          <Progress value={town.stats.schools.high * 10} className="h-1.5 [&>[data-slot=progress-indicator]]:bg-secondary" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                        <GraduationCap className="w-4 h-4" />
                        <span>Based on recent district ratings</span>
                      </div>
                    </TabsContent>

                    <TabsContent value="commute" className="space-y-5 mt-0">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-muted/30 p-3 rounded-lg text-center">
                          <div className="text-2xl font-bold text-primary">
                            {town.stats.commute.time}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Avg. to NYC
                          </div>
                        </div>
                        <div className="bg-muted/30 p-3 rounded-lg text-center flex flex-col items-center justify-center">
                          {town.stats.commute.type.includes("TrainFront") ? <TrainFront className="w-6 h-6 text-secondary mb-1" /> : <Car className="w-6 h-6 text-secondary mb-1" />}
                          <div className="text-xs text-muted-foreground">
                            {town.stats.commute.type}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs font-medium">
                          <span>Commuter Ease Score</span>
                          <span className="text-muted-foreground">
                            {town.stats.commute.ease}%
                          </span>
                        </div>
                        <Progress value={town.stats.commute.ease} className="h-1.5 [&>[data-slot=progress-indicator]]:bg-secondary" />
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
                <CardFooter className="pt-0">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full border-primary/20 hover:bg-primary/5 hover:text-primary group">
                        <House className="w-4 h-4 mr-2 group-hover:text-secondary transition-colors" />
                        View Current Listings
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Info className="w-5 h-5 text-secondary" />
                          Listings for {town.name}
                        </DialogTitle>
                        <DialogDescription>
                          We are currently updating our real-time feed for{" "}
                          {town.name}.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-6">
                        <div className="bg-muted/30 p-4 rounded-lg border border-border/50 text-center">
                          <p className="text-sm text-muted-foreground mb-4">
                            Want to be notified when new homes hit the market in{" "}
                            <span className="font-semibold text-foreground">
                              {town.name}
                            </span>
                            ?
                          </p>
                          <Button className="w-full sm:w-auto">
                            Contact Chris for Alerts
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                      <DialogFooter className="sm:justify-start">
                        <DialogClose asChild>
                          <Button type="button" variant="secondary">
                            Close
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}